import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Textarea } from "../components/ui/textarea";
import { FileText, Wand2, Loader2, Sparkles, AlertTriangle, ArrowLeft, BookOpen, Car, CreditCard, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const catIcons = { immigration: BookOpen, social_security: CreditCard, dmv: Car };

export default function FormExplainer() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.split("-")[0] || "en";
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState({});
  const [explanations, setExplanations] = useState({});
  const [userContext, setUserContext] = useState({});

  const catLabels = {
    immigration: t("pages.catImmigration"),
    social_security: t("pages.catSocialSecurity"),
    dmv: t("pages.catDmv"),
  };

  const difficultyLabels = {
    easy: t("pages.diffEasy"),
    medium: t("pages.diffMedium"),
    hard: t("pages.diffHard"),
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (formId) {
          const res = await axios.get(`${API}/forms/${formId}`, { params: { locale } });
          setSelectedForm(res.data);
        } else {
          const res = await axios.get(`${API}/forms`, { params: { locale } });
          setForms(res.data);
        }
      } catch { toast.error("Failed to load forms"); }
      setLoading(false);
    };
    load();
  }, [formId, locale]);

  const handleExplain = async (fieldId) => {
    setExplaining((p) => ({ ...p, [fieldId]: true }));
    try {
      const res = await axios.post(
        `${API}/forms/${formId}/explain`,
        { field_id: fieldId, user_context: userContext[fieldId] || "", locale },
        { withCredentials: true }
      );
      setExplanations((p) => ({ ...p, [fieldId]: res.data.explanation }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get explanation");
    }
    setExplaining((p) => ({ ...p, [fieldId]: false }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="forms-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Form list view - grouped by category
  if (!formId && !selectedForm) {
    const grouped = {};
    forms.forEach((f) => {
      const cat = f.category || "other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(f);
    });

    const catOrder = ["immigration", "social_security", "dmv"];

    return (
      <main className="min-h-screen bg-background" data-testid="forms-list-page">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.formsTitle")}</h1>
            <p className="text-base text-slate-500">{t("pages.formsSubtitle")}</p>
          </div>

          {catOrder.map((cat) => {
            const catForms = grouped[cat];
            if (!catForms || catForms.length === 0) return null;
            const Icon = catIcons[cat] || FileText;
            return (
              <div key={cat} className="mb-10" data-testid={`category-${cat}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-slate-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-heading">{catLabels[cat] || cat}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {catForms.map((form) => (
                    <Link to={`/forms/${form.form_id}`} key={form.form_id}>
                      <Card className="border-slate-100 hover:border-teal-200 hover:shadow-md transition-all duration-300 h-full card-hover" data-testid={`form-select-${form.form_id}`}>
                        <CardContent className="p-6">
                          <Badge variant="outline" className="text-xs capitalize mb-3">{catLabels[form.category] || form.agency}</Badge>
                          <h3 className="text-lg font-bold text-slate-900 mb-1">{form.code}</h3>
                          <p className="text-sm text-slate-700 mb-2">{form.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-2 mb-4">{form.description}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {form.estimated_time}</span>
                            <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {form.filing_fee}</span>
                          </div>
                          <Button className="w-full mt-4 bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium" size="sm" data-testid={`start-wizard-${form.form_id}`}>
                            <Wand2 className="mr-2 h-3.5 w-3.5" /> {t("pages.startWizard")}
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    );
  }

  // Form detail view
  const form = selectedForm;
  if (!form) return null;

  const sections = {};
  (form.fields || []).forEach((f) => {
    const sec = f.section || "General";
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(f);
  });

  return (
    <main className="min-h-screen bg-background" data-testid="form-detail-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Button variant="ghost" className="mb-6 text-slate-600" onClick={() => navigate("/forms")} data-testid="back-to-forms">
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("pages.backToForms")}
        </Button>

        <div className="mb-8">
          <div className="flex items-start gap-4 mb-4">
            <Badge variant="outline" className="text-xs capitalize">{catLabels[form.category] || form.category?.replace("_", " ")}</Badge>
            <Badge variant="outline" className="text-xs">{form.agency}</Badge>
            <Badge className={`text-xs ${form.difficulty === "hard" ? "bg-red-100 text-red-700" : form.difficulty === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {difficultyLabels[form.difficulty] || form.difficulty}
            </Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{form.code} - {form.name}</h1>
          <p className="text-base text-slate-600 mb-4">{form.description}</p>
          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {form.estimated_time}</span>
            <span className="flex items-center gap-1"><DollarSign className="h-4 w-4" /> {form.filing_fee}</span>
            <span>{t("pages.fields")} {form.fields?.length || 0}</span>
          </div>
        </div>

        <div className="flex gap-3 mb-8">
          <Link to={`/wizard/${form.form_id}`}>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="start-wizard-btn">
              <Wand2 className="mr-2 h-4 w-4" /> {t("pages.startGuided")}
            </Button>
          </Link>
        </div>

        {Object.entries(sections).map(([section, fields]) => (
          <div key={section} className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-100">{section}</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {fields.map((field) => (
                <AccordionItem key={field.field_id} value={field.field_id} className="border border-slate-100 rounded-xl px-1 data-[state=open]:border-teal-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid={`field-trigger-${field.field_id}`}>
                    <div className="flex items-center gap-3 text-left">
                      <span className="text-sm font-semibold text-slate-900">{field.label}</span>
                      {field.required && <Badge variant="outline" className="text-xs text-red-500 border-red-200">{t("pages.fieldRequired")}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <p className="text-sm text-slate-600 mb-3">{field.description}</p>
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-slate-700"><strong>{t("pages.quickTip")}</strong> {field.help_text}</p>
                    </div>
                    {field.common_mistakes?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-800">{t("pages.commonMistakes")}</span>
                        </div>
                        <ul className="space-y-1">
                          {field.common_mistakes.map((m, i) => (
                            <li key={i} className="text-sm text-amber-700 flex items-start gap-1.5">
                              <span className="text-amber-400 mt-1">-</span> {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="space-y-3">
                      <Textarea
                        placeholder={t("pages.fieldQuestion")}
                        className="text-sm"
                        value={userContext[field.field_id] || ""}
                        onChange={(e) => setUserContext((p) => ({ ...p, [field.field_id]: e.target.value }))}
                        data-testid={`field-question-${field.field_id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleExplain(field.field_id)}
                        disabled={explaining[field.field_id]}
                        className="bg-teal-700 hover:bg-teal-800 text-white"
                        data-testid={`explain-btn-${field.field_id}`}
                      >
                        {explaining[field.field_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Sparkles className="h-3.5 w-3.5 mr-2" />}
                        {t("pages.explainWithAI")}
                      </Button>
                    </div>
                    {explanations[field.field_id] && (
                      <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-4" data-testid={`explanation-${field.field_id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-4 w-4 text-teal-600" />
                          <span className="text-sm font-medium text-teal-800">{t("pages.aiExplanation")}</span>
                        </div>
                        <div className="text-sm text-teal-900 whitespace-pre-wrap leading-relaxed">
                          {explanations[field.field_id]}
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </main>
  );
}
